import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import UserTenantRoleEntity from '@/entities/userTenantRole';
import { catchGraphQlError } from '@/lib/catchError';

  import UserEntity from '@/entities/user';
  import TenantEntity from '@/entities/tenant';
  import RoleEntity from '@/entities/role';
  import ProjectEntity from '@/entities/project';

function UserTenantRoleBaseUpdateForm({ formOnly = false, onClose, Entity = UserTenantRoleEntity, id: propId, ...props }: BaseFormUpdateProps<typeof UserTenantRoleEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = UserTenantRoleEntity.model.useGet({
    variables: {
      id,
            withUser: true,
            withTenant: true,
            withRole: true,
            withProject: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = UserTenantRoleEntity.model.useUpdate();

  const relationsOptionsQuery = UserTenantRoleEntity.model.useRelationsOptions({
    variables: {
          withUsers: true,
          withTenants: true,
          withRoles: true,
          withProjects: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              UserEntity,
              TenantEntity,
              RoleEntity,
              ProjectEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:userTenantRole') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateUserTenantRole);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.userTenantRole}
    />
  );
}

export default UserTenantRoleBaseUpdateForm;
